import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/max";

const GREETING = "Hi! Welcome to Solufy. I’m your ERP Assistant. I’ll ask a few quick questions to help our team understand what you need.";
const QUESTIONS = [
	{ key: "name", label: "Full name", question: "What’s your full name?", limit: 120, min: 2, autocomplete: "name" },
	{ key: "email", label: "Work email", question: "What’s your work email address?", limit: 140, mode: "email", autocomplete: "email" },
	{ key: "phone", label: "Phone number", question: "What phone number can our team reach you on? Select your country code and enter your number.", limit: 30, mode: "tel", autocomplete: "tel-national" },
	{ key: "company", label: "Company", question: "What’s your company name?", limit: 140, autocomplete: "organization" },
	{ key: "service", label: "Service needed", question: "Which service do you need? For example: ERPNext implementation, customization, integration, or support.", limit: 120 },
	{ key: "message", label: "Requirements", question: "Finally, tell us about your requirements. I’ll send your answers to our team when you submit this answer.", limit: 1200 },
];

export function mountLeadChat(root = document) {
	root.querySelectorAll("[data-lead-chat]").forEach((element) => {
		if (element.dataset.mounted) return;
		element.dataset.mounted = "true";
		new LeadChat(element);
	});
}

class LeadChat {
	constructor(element) {
		this.element = element;
		this.log = element.querySelector("[data-chat-log]");
		this.form = element.querySelector("[data-chat-form]");
		this.answer = element.querySelector("[data-chat-answer]");
		this.label = element.querySelector("[data-chat-label]");
		this.error = element.querySelector("[data-chat-error]");
		this.send = element.querySelector("[data-chat-send]");
		this.back = element.querySelector("[data-chat-back]");
		this.country = element.querySelector("[data-chat-country]");
		this.countryWrap = element.querySelector("[data-chat-country-wrap]");
		this.answers = {};
		this.step = 0;
		this.pending = false;
		this.started = false;
		this.thinking = false;
		this.setupCountries();
		document.addEventListener("solufy:chat-open", () => this.open());
		this.answer.addEventListener("keydown", (event) => {
			if (event.key !== "Enter" || event.shiftKey || event.isComposing) return;
			event.preventDefault();
			if (!this.pending) this.form.requestSubmit();
		});
		this.form.addEventListener("submit", (event) => this.advance(event));
		this.back.addEventListener("click", () => {
			if (this.pending || this.step === 0) return;
			this.step = Math.min(this.step - 1, QUESTIONS.length - 1);
			this.render();
		});
		if (document.getElementById("ccRoot")?.classList.contains("is-open")) this.open();
	}

	open() {
		if (this.started) return;
		this.started = true;
		this.askNextQuestion();
	}

	askNextQuestion() {
		this.pending = true;
		this.thinking = true;
		this.render();
		window.setTimeout(() => {
			this.pending = false;
			this.thinking = false;
			this.render();
		}, 2000);
	}

	setupCountries() {
		const names = new Intl.DisplayNames(["en"], { type: "region" });
		getCountries().sort((a, b) => names.of(a).localeCompare(names.of(b))).forEach((code) => {
			const option = document.createElement("option");
			option.value = code;
			option.textContent = `${names.of(code)} (+${getCountryCallingCode(code)})`;
			this.country.append(option);
		});
		this.country.value = "IN";
	}

	advance(event) {
		event.preventDefault();
		if (this.pending || !this.started) return;
		if (this.step === QUESTIONS.length) return this.submit();
		const question = QUESTIONS[this.step];
		const value = this.answer.value.trim();
		const validation = this.validate(question, value);
		if (validation) {
			this.error.textContent = validation;
			return;
		}

		this.answers[question.key] = value;
		if (question.key === "phone") {
			this.answers.phone = this.parsePhone(value).number;
			this.answers.phone_country = this.country.value;
		}
		this.step += 1;
		if (this.step === QUESTIONS.length) {
			this.render();
			this.submit();
		} else {
			this.askNextQuestion();
		}
	}

	async submit() {
		this.pending = true;
		this.send.disabled = true;
		this.back.disabled = true;
		this.send.textContent = "Sending…";
		this.error.textContent = "";
		this.element.setAttribute("aria-busy", "true");
		try {
			const params = new URLSearchParams(this.answers);
			params.set("message", `Website chatbot inquiry\n\n${this.answers.message}`);
			params.set("page", window.location.pathname);
			params.set("referrer", document.referrer);
			const query = new URLSearchParams(window.location.search);
			["utm_source", "utm_medium", "utm_campaign"].forEach((key) => params.set(key, query.get(key) || ""));
			const token = window.frappe?.csrf_token;
			const response = await fetch("/api/method/solufy_website.api.leads.create_lead", {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
					...(token && token !== "None" ? { "X-Frappe-CSRF-Token": token } : {}),
				},
				body: params,
			});
			const result = await response.json();
			if (!response.ok || !result.message?.ok || !result.message?.lead) {
				let errMsg = "";
				if (result._server_messages) {
					try {
						errMsg = JSON.parse(result._server_messages).map((m) => JSON.parse(m).message).join(" ");
					} catch (e) {}
				}
				if (!errMsg) errMsg = result.message?.message || (response.status === 429 ? "Too many requests. Please try again later, or contact us by phone or email." : "We couldn’t save your inquiry. Your answers are still here; please try again.");
				throw new Error(errMsg);
			}
			this.addMessage("Thank you! Your inquiry has been saved. Our team will contact you shortly.");
			this.form.hidden = true;
			document.getElementById("ccClose")?.focus({ preventScroll: true });
		} catch (error) {
			this.error.textContent = error.message;
			this.send.textContent = "Retry sending";
			this.send.disabled = false;
			this.back.disabled = false;
		} finally {
			this.pending = false;
			this.element.setAttribute("aria-busy", "false");
		}
	}

	render() {
		this.log.replaceChildren();
		this.addMessage(GREETING);
		QUESTIONS.forEach((question, index) => {
			if (index > this.step || (this.thinking && index === this.step)) return;
			this.addMessage(question.question);
			if (index < this.step) this.addMessage(this.answers[question.key], true);
		});
		if (this.thinking) {
			this.addMessage("Thinking…");
			this.log.lastElementChild.classList.add("lead-chat-thinking");
		}
		const question = QUESTIONS[this.step];
		this.form.hidden = this.thinking;
		this.send.disabled = this.pending;
		this.countryWrap.hidden = question?.key !== "phone";
		this.error.textContent = "";
		this.answer.disabled = !question;
		this.answer.hidden = !question;
		this.label.hidden = !question;
		this.back.disabled = this.step === 0;
		this.send.textContent = this.step >= QUESTIONS.length - 1 ? "Send inquiry" : "Send answer";
		if (!question) return;
		this.label.textContent = `${question.label} (${this.step + 1} of ${QUESTIONS.length})`;
		this.answer.maxLength = question.limit;
		this.answer.inputMode = question.mode || "text";
		this.answer.autocomplete = question.autocomplete || "off";
		this.answer.value = this.answers[question.key] || "";
		if (question.key === "phone" && this.answers.phone) {
			this.country.value = this.answers.phone_country;
			this.answer.value = this.parsePhone(this.answers.phone)?.nationalNumber || this.answers.phone;
		}
		if (!this.thinking && document.getElementById("ccRoot")?.classList.contains("is-open")) this.answer.focus({ preventScroll: true });
	}

	parsePhone(value) {
		return parsePhoneNumberFromString(value, { defaultCountry: this.country.value, extract: false });
	}

	validate(question, value) {
		if (value.length < (question.min || 1)) return `Please enter your ${question.label.toLowerCase()}.`;
		if (question.key === "email") {
			const input = document.createElement("input");
			input.type = "email";
			input.value = value;
			if (!input.checkValidity() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value.includes("..")) return "Please enter a valid email address, for example name@company.com.";
		}
		if (question.key === "phone") {
			const phone = this.parsePhone(value);
			if (!/^[+\d\s().-]+$/.test(value) || !phone?.isValid() || phone.country !== this.country.value) return "Please enter a valid phone number for the selected country code.";
		}
		return "";
	}

	addMessage(text, visitor = false) {
		const bubble = document.createElement("p");
		bubble.className = visitor ? "lead-chat-message lead-chat-message--visitor" : "lead-chat-message";
		bubble.textContent = text;
		this.log.append(bubble);
		this.log.scrollTop = this.log.scrollHeight;
	}
}
